import type { House, Person, Settlement } from '../engine/settlement';
import { removePersonById, recordConsumed } from '../engine/settlement';
import { getGameConfig } from '../config/gameConfig';

type PeopleById = Map<string, Person>;

// Remove resources from a house store and record them as consumed by the settlement.
function consumeFromHouse(
    settlement: Settlement,
    house: House,
    key: 'food' | 'wood' | 'charcoal',
    amount: number,
): number {
    const available = house.storage[key];
    if (available <= 0) return 0;
    const take = Math.min(available, amount);
    house.storage[key] -= take;
    if (take > 0) recordConsumed(settlement, key, take);
    return take;
}

function removePerson(settlement: Settlement, person: Person, cause: 'starvation' | 'cold'): void {
    const removed = removePersonById(settlement, person.id);
    if (!removed) return;
    if (cause === 'starvation') settlement.production.population.lastStarvationDeaths += 1;
    else settlement.production.population.lastColdDeaths += 1;
}

function adjustHunger(person: Person, amount: number): void {
    person.hunger = Math.max(0, person.hunger + amount);
}

// Feed each person from the house store. Hunger rises every interval and meals reduce it.
function runHousePeopleFoodTick(
    settlement: Settlement,
    house: House,
    peopleById: PeopleById,
    tickIndex: number,
): boolean {
    if (house.people.length <= 0) return false;

    const config = getGameConfig();

    const hungerStep = config.population.hunger.hungerStep;
    const eatThreshold = config.population.hunger.eatThreshold;
    const maxHunger = config.population.hunger.maxHunger;
    const foodQty = config.population.hunger.foodQty;
    const foodStep = config.population.hunger.foodStep;
    const hungerInterval = config.population.hunger.hungerInterval;

    if (tickIndex % hungerInterval !== 0) {
        return false;
    }

    let changed = false;
    const peopleIds = house.people.slice();
    for (const personId of peopleIds) {
        const person = peopleById.get(personId);
        if (!person) {
            console.warn(`Person of ID ${personId} was not found`);
            continue;
        }

        // Hunger grows on each interval before any eating happens.
        adjustHunger(person, hungerStep);
        changed = true;

        // Eat only when hunger passes the threshold and a full meal is available.
        if (person.hunger >= eatThreshold && house.storage.food >= foodQty) {
            const eaten = consumeFromHouse(settlement, house, 'food', foodQty);
            if (eaten >= foodQty) {
                adjustHunger(person, -foodQty * foodStep);
            }
        }

        // Starvation happens after eating attempts on this interval.
        if (person.hunger >= maxHunger) {
            removePerson(settlement, person, 'starvation');
            changed = true;
        }
    }

    return changed;
}

function runHouseColdTick(house: House): boolean {
    const config = getGameConfig();

    if (house.warmth <= 0) {
        return false;
    }

    house.warmth = Math.max(0, house.warmth - config.heat.coldStep[house.type]);

    return true;
}

function runHouseWarmTick(house: House): boolean {
    const config = getGameConfig();
    let changed = false;

    const maxWarmth = config.heat.maxWarmth[house.type];

    if (house.warmth >= maxWarmth) {
        return false;
    }

    if (house.storage.charcoal > 0) {
        if (house.warmth + config.heat.warmth.charcoal <= maxWarmth) {
            house.storage.charcoal--;
            house.warmth += config.heat.warmth.charcoal;
            changed = true;
        }
    } else if (house.storage.wood > 0) {
        if (house.warmth + config.heat.warmth.wood <= maxWarmth) {
            house.storage.wood--;
            house.warmth += config.heat.warmth.wood;
            changed = true;
        }
    }

    return changed;
}

function runHousePeopleWarmthTick(
    settlement: Settlement,
    house: House,
    peopleById: PeopleById,
): boolean {
    const config = getGameConfig();

    const threshold = config.population.warmth.coldDebtThreshold;
    const coldStep = config.population.warmth.coldStep;
    const warmthStep = config.population.warmth.warmthStep;

    const people = house.people.map((pID) => peopleById.get(pID)!!);

    if (house.warmth <= threshold) {
        for (let person of people) {
            person.warmth = Math.max(0, person?.warmth - coldStep);
        }

        for (let person of people) {
            if (person.warmth <= 0) {
                removePerson(settlement, person, 'cold');
            }
        }

        // if function was called and it's cold, something will happen, cold/death
        return true;
    }

    let changed = false;

    for (let person of people) {
        if (person.warmth === config.population.warmth.maxWarmth) {
            continue;
        }

        person.warmth = Math.min(config.population.warmth.maxWarmth, person.warmth + warmthStep);
        changed = true;
    }

    return changed;
}

// Spend house fuel to cover heat needs; when heat is missing residents lose warmth.
function runWarmthTick(
    settlement: Settlement,
    house: House,
    peopleById: PeopleById,
    tickIndex: number,
): boolean {
    // TODO: call it even if there are no people in house, cause house gets cold anyway
    if (house.people.length <= 0) return false;

    let changed = false;

    const config = getGameConfig();
    const peopleInterval = config.population.warmth.coldInterval;
    const houseColdInterval = config.heat.coldInterval;
    const houseWarmInterval = config.heat.warmInterval;

    if (tickIndex % houseColdInterval === 0 && runHouseColdTick(house)) {
        changed = true;
    }

    if (tickIndex % houseWarmInterval === 0 && runHouseWarmTick(house)) {
        changed = true;
    }

    if (
        tickIndex % peopleInterval === 0 &&
        runHousePeopleWarmthTick(settlement, house, peopleById)
    ) {
        changed = true;
    }

    return changed;
}

export function runConsumptionTick(settlement: Settlement, tickIndex: number): boolean {
    let changed = false;

    // Reset tick-local counters for alerts/logging.
    settlement.production.population.lastStarvationDeaths = 0;
    settlement.production.population.lastColdDeaths = 0;

    // Build a lookup map so we can mutate people by id efficiently.
    const peopleById = new Map(settlement.people.map((person) => [person.id, person]));
    settlement.houses.forEach((house) => {
        // Food consumption and starvation check.
        if (runHousePeopleFoodTick(settlement, house, peopleById, tickIndex)) {
            changed = true;
        }

        // Warmth consumption and cold death check.
        if (runWarmthTick(settlement, house, peopleById, tickIndex)) {
            changed = true;
        }
    });

    return changed;
}
